import type { SampleType } from '@/model/SampleType.model';
import type { ValidationData } from '@/utils/sampleTypeValidation';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';

import {
  buildSampleTypeValidator,
  buildSampleTypeErrorMessage,
  buildLabelValidator,
  buildLabelErrorMessage,
  buildDetailsValidator,
  buildAfterRendererCallback as buildSharedAfterRendererCallback,
} from './BulkEditSampleTypes.utils.shared';

interface HotTableRefLike {
  hotInstance?: unknown;
}

interface BuildColumnsParams {
  availableDetailOptions: Array<string>;
  validationData: ValidationData | null;
  sampleTypes: Array<SampleType>;
  hotTableRef: { current: HotTableRefLike | null };
}

export const buildColumns = ({
  availableDetailOptions,
  validationData,
  sampleTypes,
  hotTableRef,
}: BuildColumnsParams): SpreadsheetSettings['columns'] => {
  const validatorCtx = { validationData, sampleTypes, hotTableRef };

  return [
    {
      data: 0,
      value: 'Sample type',
      type: 'text',
      className: 'htMiddle',
      width: 140,
      validator: buildSampleTypeValidator(validatorCtx),
      validationErrorMessage: buildSampleTypeErrorMessage(validationData),
      linkedValidationColumns: ['0'],
    },
    {
      data: 1,
      value: 'Label',
      type: 'text',
      className: 'htMiddle',
      width: 120,
      validator: buildLabelValidator(validatorCtx),
      validationErrorMessage: buildLabelErrorMessage(validationData),
      linkedValidationColumns: ['1'],
    },
    {
      data: 2,
      value: 'Details',
      type: MultiSelectEditor.type,
      className: 'htMiddle',
      width: 280,
      options: availableDetailOptions,
      isMultiSelect: true,
      validator: buildDetailsValidator(availableDetailOptions),
    },
  ];
};

export const initBulkEditSampleTypesSettings = (columns: SpreadsheetSettings['columns']): SpreadsheetSettings => ({
  columns,
  colHeaders: ['Sample type', 'Label', 'Details'],
  rowHeaders: true,
  manualColumnResize: true,
  contextMenu: false,
  outsideClickDeselects: false,
  height: 600,
  rowHeights: 50,
  className: 'htMiddle',
  stretchH: 'all',
});

export { buildCellsCallback as buildCellsCallback } from './BulkEditSampleTypes.utils.shared';

export const buildAfterRendererCallback = (
  sampleTypes: Array<SampleType>
): ((
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: object
) => void) => {
  const sharedCallback = buildSharedAfterRendererCallback(sampleTypes);

  return function (
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: unknown,
    cellProperties: object
  ): void {
    sharedCallback(td, row, col, prop, value, cellProperties as { readOnly?: boolean });
  };
};
